import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listAdminInspectionReports,
  uploadAdminInspectionReport,
  downloadAdminInspectionReportFile,
} from '@/services/portalInspectionReports';
import { ClipboardCheck, Download, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg';

function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

export default function AdminInspectionReports() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [customerLookup, setCustomerLookup] = useState('');
  const [title, setTitle] = useState('');
  const inputRef = useRef(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await listAdminInspectionReports();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message;
      setError(typeof msg === 'string' ? msg : 'Liste yüklenemedi.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const onDownload = async (row) => {
    const id = row._id;
    setDownloadingId(id);
    setError('');
    try {
      await downloadAdminInspectionReportFile(id, row.file_name);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message;
      setError(typeof msg === 'string' ? msg : 'İndirme başarısız.');
    } finally {
      setDownloadingId(null);
    }
  };

  const onSubmitUpload = async (e) => {
    e.preventDefault();
    setUploadMsg('');
    setError('');
    const file = inputRef.current?.files?.[0];
    if (!file) {
      setError('Lütfen bir dosya seçin.');
      return;
    }
    if (!String(customerLookup).trim()) {
      setError('Lütfen müşteri adını veya e-postayı girin.');
      return;
    }
    setUploading(true);
    try {
      await uploadAdminInspectionReport(file, customerLookup, title);
      setUploadMsg('Muayene raporu yüklendi.');
      setTitle('');
      if (inputRef.current) inputRef.current.value = '';
      await loadReports();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message;
      setError(typeof msg === 'string' ? msg : 'Yükleme başarısız.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-inspection-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Inspection reports</h1>
          <p className="text-slate-500 text-sm mt-0.5">Müşterilere atanmış muayene belgelerini yükleyin</p>
        </div>
        <Button type="button" variant="outline" onClick={loadReports} disabled={loading}>
          Yenile
        </Button>
      </div>

      <form
        onSubmit={onSubmitUpload}
        className="bg-white rounded-xl border border-slate-200 p-5 space-y-4"
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <ClipboardCheck className="w-4 h-4 text-sky-600" />
          Yeni rapor yükle
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500 uppercase">Müşteri</label>
            <Input
              value={customerLookup}
              onChange={(ev) => setCustomerLookup(ev.target.value)}
              placeholder="Şirket adı, iletişim adı veya e-posta (tam eşleşme)"
              autoComplete="off"
              data-testid="admin-inspection-customer-input"
            />
            <p className="text-xs text-slate-400">
              Kayıtlı müşteriyle birebir aynı olmalıdır. Birden fazla eşleşme olursa e-posta girin.
            </p>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-500 uppercase">Başlık (isteğe bağlı)</label>
            <Input
              value={title}
              onChange={(ev) => setTitle(ev.target.value)}
              placeholder="Örn. Lot #12 muayenesi"
              data-testid="admin-inspection-title"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1 space-y-1.5">
            <label className="text-xs font-medium text-slate-500 uppercase">Dosya</label>
            <Input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              data-testid="admin-inspection-file"
            />
          </div>
          <Button type="submit" disabled={uploading} className="gap-2 shrink-0">
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Yükleniyor…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Yükle
              </>
            )}
          </Button>
        </div>
      </form>

      {uploadMsg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg px-4 py-3">
          {uploadMsg}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-500 text-sm">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Yükleniyor…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <ClipboardCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Henüz muayene raporu yok.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full min-w-[900px]" data-testid="admin-inspection-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Müşteri</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Başlık</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Dosya</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Boyut</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Yükleyen</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase">Tarih</th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold text-slate-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((row) => (
                <tr key={row._id} className="hover:bg-slate-50/80">
                  <td className="px-5 py-4 text-sm font-medium text-slate-900">{row.customer_name || '—'}</td>
                  <td className="px-5 py-4 text-sm text-slate-600 max-w-[180px] truncate" title={row.title || ''}>
                    {row.title || '—'}
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-700 max-w-[200px] truncate" title={row.file_name}>
                    {row.file_name || '—'}
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600 tabular-nums">
                    {row.size_bytes != null ? `${(row.size_bytes / 1024).toFixed(1)} KB` : '—'}
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600">{row.uploaded_by_name || '—'}</td>
                  <td className="px-5 py-4 text-sm text-slate-600 whitespace-nowrap">{formatDate(row.created_at)}</td>
                  <td className="px-5 py-4 text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      disabled={downloadingId === row._id}
                      onClick={() => onDownload(row)}
                    >
                      {downloadingId === row._id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      İndir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
